import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import DateRangePicker from "../components/DateRangePicker";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_COLORS = [
  "#2d6a4f", "#52b788", "#f4a261", "#e76f51",
  "#457b9d", "#a8dadc", "#e9c46a", "#264653"
];

function getDateRange(filter) {
  const now = new Date();
  const pad = n => String(n).padStart(2, "0");
  const fmt = d => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
  const today = fmt(now);

  if (filter === "week") {
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { from: fmt(start), to: today };
  }
  if (filter === "month") {
    return { from: `${now.getFullYear()}-${pad(now.getMonth()+1)}-01`, to: today };
  }
  if (filter === "year") {
    return { from: `${now.getFullYear()}-01-01`, to: today };
  }
  return { from: null, to: null };
}

export default function Dashboard({ user, onNavigate }) {
  const [filter, setFilter]           = useState("month");
  const [customRange, setCustomRange] = useState(null);
  const [showPicker, setShowPicker]   = useState(false);
  const [data, setData]               = useState(null);
  const [prevData, setPrevData]       = useState(null);
  const [loading, setLoading]         = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryItems, setCategoryItems] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayItems, setDayItems] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);

  function getDateRangeParams() {
    if (filter === "custom" && customRange) {
      return { from: customRange.from, to: customRange.to };
    }
    return getDateRange(filter);
  }

  function loadCategoryItems(category) {
    const isSame = selectedCategory === category;
    if (isSame) {
      setSelectedCategory(null);
      setCategoryItems([]);
      return;
    }

    setSelectedDay(null);
    setDayItems([]);
    setSelectedCategory(category);
    setCategoryLoading(true);
    const { from, to } = getDateRangeParams();
    const params = new URLSearchParams();
    params.set("category", category);
    if (from) params.set("from", from);
    if (to) params.set("to", to);

    fetch(`${BACKEND}/expenses/filtered?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        setCategoryItems(d.items || []);
        setCategoryLoading(false);
      })
      .catch(() => {
        setCategoryItems([]);
        setCategoryLoading(false);
      });
  }

  function loadDayItems(day) {
    const isSame = selectedDay === day;
    if (isSame) {
      setSelectedDay(null);
      setDayItems([]);
      return;
    }

    setSelectedCategory(null);
    setCategoryItems([]);
    setSelectedDay(day);
    setDayLoading(true);
    const params = new URLSearchParams();
    params.set("from", day);
    params.set("to", day);

    fetch(`${BACKEND}/expenses/filtered?${params.toString()}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => {
        setDayItems(d.items || []);
        setDayLoading(false);
      })
      .catch(() => {
        setDayItems([]);
        setDayLoading(false);
      });
  }

  useEffect(() => {
    setLoading(true);
    setSelectedCategory(null);
    setCategoryItems([]);
    setSelectedDay(null);
    setDayItems([]);
    let from, to;

    if (filter === "custom" && customRange) {
      from = customRange.from;
      to = customRange.to;
    } else {
      ({ from, to } = getDateRange(filter));
    }

    const params = from ? `?from=${from}&to=${to}` : "";
    fetch(`${BACKEND}/expenses/stats${params}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); });
  }, [filter, customRange]);

  useEffect(() => {
    const now = new Date();
    const pad = n => String(n).padStart(2, "0");
    const firstThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonth = new Date(firstThisMonth - 1);
    const from = `${lastMonth.getFullYear()}-${pad(lastMonth.getMonth()+1)}-01`;
    const to = `${lastMonth.getFullYear()}-${pad(lastMonth.getMonth()+1)}-${pad(lastMonth.getDate())}`;
    fetch(`${BACKEND}/expenses/stats?from=${from}&to=${to}`, { credentials: "include" })
      .then(r => r.json())
      .then(d => setPrevData(d));
  }, []);

  function handleCustomApply(range) {
    setCustomRange(range);
    setFilter("custom");
    setShowPicker(false);
  }

  const total = parseFloat(data?.total || 0);
  const prevTotal = parseFloat(prevData?.total || 0);
  const pctChange = prevTotal > 0 ? (((total - prevTotal) / prevTotal) * 100).toFixed(1) : null;
  const topCategory = data?.byCategory?.[0];
  const maxTimeline = Math.max(...(data?.timeline?.map(t => t.total) || [1]));

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return "Good morning";
    if (h < 18) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} active="dashboard" onNavigate={onNavigate} />

      <main className="dashboard-main">
        <div className="dashboard-header">
          <div className="dashboard-greeting">
            {greeting()}, {user?.name?.split(" ")[0] || "there"} 👋
          </div>
          <div className="dashboard-subtext">Here's your spending overview</div>
        </div>

        {/* Time Filter */}
        <div className="time-filter">
          {[
            { key: "week",  label: "This Week"  },
            { key: "month", label: "This Month" },
            { key: "year",  label: "This Year"  },
            { key: "all",   label: "All Time"   },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`time-btn ${filter === key ? "active" : ""}`}
              onClick={() => { setFilter(key); setCustomRange(null); setShowPicker(false); }}
            >
              {label}
            </button>
          ))}

          {/* Custom Range */}
          <div className="date-picker-wrapper">
            <button
              className={`time-btn ${filter === "custom" ? "active" : ""}`}
              onClick={() => setShowPicker(p => !p)}
            >
              {filter === "custom" && customRange ? customRange.label : "Custom Range 📅"}
            </button>
            {showPicker && (
              <DateRangePicker
                onApply={handleCustomApply}
                onClose={() => setShowPicker(false)}
              />
            )}
          </div>
        </div>

        {loading ? (
          <div className="dashboard-loading">Loading your data…</div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="summary-cards">
              <div className="summary-card accent">
                <div className="summary-card-label">Total Spend</div>
                <div className="summary-card-value">
                  ${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                {pctChange !== null && filter === "month" && (
                  <div className={`summary-card-change ${parseFloat(pctChange) <= 0 ? "positive" : "negative"}`}>
                    {parseFloat(pctChange) <= 0 ? "↓" : "↑"} {Math.abs(pctChange)}% vs last month
                  </div>
                )}
              </div>

              <div className="summary-card">
                <div className="summary-card-label">Top Category</div>
                <div className="summary-card-value" style={{ fontSize: "22px", paddingTop: "6px" }}>
                  {topCategory?.category || "—"}
                </div>
                <div className="summary-card-sub">
                  {topCategory ? `$${parseFloat(topCategory.total).toFixed(2)} spent` : "No data yet"}
                </div>
              </div>

              <div className="summary-card">
                <div className="summary-card-label">Transactions</div>
                <div className="summary-card-value">{data?.timeline?.length || 0}</div>
                <div className="summary-card-sub">days with spending</div>
              </div>
            </div>

            {/* Charts Row */}
            <div className="charts-row">
              <div className="chart-card">
                <div className="chart-card-title">Spending Over Time</div>
                {data?.timeline?.length > 0 ? (
                  <>
                    <div className="bar-chart">
                      {data.timeline.map((t, i) => {
                        const heightPct = maxTimeline > 0 ? (parseFloat(t.total) / maxTimeline) * 100 : 0;
                        const isToday = t.day === new Date().toISOString().split("T")[0];
                        const isSelected = selectedDay === t.day;
                        return (
                          <div
                            className="bar-chart-col"
                            key={i}
                            title={`${t.day}: $${parseFloat(t.total).toFixed(2)}`}
                            onClick={() => loadDayItems(t.day)}
                          >
                            <div className={`bar-chart-bar ${isToday ? "today" : ""} ${isSelected ? "selected-day" : ""}`} style={{ height: `${heightPct}%` }} />
                            {data.timeline.length <= 14 && (
                              <div className="bar-chart-label">{t.day.slice(5)}</div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {selectedDay && (
                      <div className="category-detail-panel">
                        <div className="category-detail-header">
                          <div>
                            <div className="category-detail-title">Purchases on {selectedDay}</div>
                            <div className="category-detail-sub">
                              {dayLoading ? "Loading purchases…" : `${dayItems.length} purchase${dayItems.length === 1 ? "" : "s"}`}
                            </div>
                          </div>
                          <button className="category-detail-close" onClick={() => setSelectedDay(null)}>
                            Close
                          </button>
                        </div>
                        {dayLoading ? (
                          <div className="dashboard-loading">Loading purchases…</div>
                        ) : dayItems.length > 0 ? (
                          <div className="category-detail-list">
                            {dayItems.map(item => (
                              <div className="category-detail-row" key={item.id}>
                                <div>
                                  <div className="category-detail-item">{item.item || item.store || "Untitled"}</div>
                                  <div className="category-detail-meta">{item.date ? item.date.slice(0, 10) : item.date} · {item.store || "Unknown store"}</div>
                                </div>
                                <div className="category-detail-price">${parseFloat(item.price).toFixed(2)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="dashboard-loading">No purchases found for this day.</div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="dashboard-loading">No spending data for this period</div>
                )}
              </div>

              <div className="category-card">
                <div className="category-card-title">By Category</div>
                {data?.byCategory?.length > 0 ? (
                  <>
                    <div className="category-list">
                      {data.byCategory.slice(0, 6).map((cat, i) => {
                        const pct = total > 0 ? (parseFloat(cat.total) / total) * 100 : 0;
                        const active = selectedCategory === cat.category;
                        return (
                          <div
                            className={`category-row ${active ? "active" : ""}`}
                            key={i}
                            onClick={() => loadCategoryItems(cat.category)}
                          >
                            <div className="category-row-top">
                              <div className="category-row-left">
                                <div className="category-dot" style={{ background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                                <span className="category-name">{cat.category}</span>
                              </div>
                              <span className="category-amount">${parseFloat(cat.total).toFixed(2)}</span>
                            </div>
                            <div className="category-bar-track">
                              <div className="category-bar-fill" style={{ width: `${pct}%`, background: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {selectedCategory && (
                      <div className="category-detail-panel">
                        <div className="category-detail-header">
                          <div>
                            <div className="category-detail-title">{selectedCategory}</div>
                            <div className="category-detail-sub">
                              {categoryLoading ? "Loading purchases…" : `${categoryItems.length} purchase${categoryItems.length === 1 ? "" : "s"}`}
                            </div>
                          </div>
                          <button className="category-detail-close" onClick={() => setSelectedCategory(null)}>
                            Close
                          </button>
                        </div>
                        {categoryLoading ? (
                          <div className="dashboard-loading">Loading purchases…</div>
                        ) : categoryItems.length > 0 ? (
                          <div className="category-detail-list">
                            {categoryItems.map(item => (
                              <div className="category-detail-row" key={item.id}>
                                <div>
                                  <div className="category-detail-item">{item.item || item.store || "Untitled"}</div>
                                  <div className="category-detail-meta">{item.date ? item.date.slice(0, 10) : item.date} · {item.store || "Unknown store"}</div>
                                </div>
                                <div className="category-detail-price">${parseFloat(item.price).toFixed(2)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="dashboard-loading">No purchases found for this category.</div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <div className="dashboard-loading">No categories yet</div>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}